// Calendar - month view with events (one-off or repeating), date difference, add/subtract days.
import { el, btn, input, field, wrap, sel, announce } from "./ui.js";
import { readJSON, writeJSON } from "../shared/store.js";
import { KEY, REPEATS, MAX_EVENTS, MAX_TITLE, cleanEvents, eventsOn, makeEvent, removeDay, removeEvent, repeatText, dayKey, fromKey, daysBetween, addDaysTo } from "./calendar-events.js";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const iso = dayKey; // local date (toISOString would give tomorrow on a US evening)

export function calendar(root) {
	const s = wrap(root); s.classList.add("rv-cal");
	const today = new Date();
	let year = today.getFullYear(), month = today.getMonth();

	const head = el("div", "rv-cal-head");
	const title = el("h2", "rv-cal-title");
	title.setAttribute("aria-live", "polite");
	const grid = el("div", "rv-cal-grid");
	const prevBtn = btn("‹", () => { month--; if (month < 0) { month = 11; year--; } draw(); }, "rv-tbtn");
	const nextBtn = btn("›", () => { month++; if (month > 11) { month = 0; year++; } draw(); }, "rv-tbtn");
	prevBtn.setAttribute("aria-label", "Previous month");
	nextBtn.setAttribute("aria-label", "Next month");
	grid.setAttribute("role", "group");
	grid.setAttribute("aria-label", "Month");
	head.append(prevBtn, title, nextBtn, btn("Today", () => { year = today.getFullYear(); month = today.getMonth(); draw(); }, "rv-tbtn"));

	// Events, kept on this device (and in backups, like the other tools).
	let events = cleanEvents(readJSON(KEY, []));
	let picked = iso(today);
	const save = () => writeJSON(KEY, events);

	function draw() {
		title.textContent = MONTHS[month] + " " + year;
		grid.textContent = "";
		DAYS.forEach((d) => grid.appendChild(el("div", "rv-cal-dow", d)));
		const first = new Date(year, month, 1).getDay();
		const days = new Date(year, month + 1, 0).getDate();
		for (let i = 0; i < first; i++) grid.appendChild(el("div", "rv-cal-cell empty"));
		for (let d = 1; d <= days; d++) {
			const key = iso(new Date(year, month, d, 12));
			const isToday = key === iso(today);
			const n = eventsOn(events, key).length;
			const cell = btn(String(d), () => { picked = key; draw(); drawDay(); }, "rv-cal-cell" + (isToday ? " today" : "") + (n ? " has" : "") + (key === picked ? " sel" : ""));
			cell.setAttribute("aria-label", MONTHS[month] + " " + d + (isToday ? ", today" : "") + (n ? ", " + n + (n === 1 ? " event" : " events") : ""));
			cell.setAttribute("aria-pressed", String(key === picked));
			if (isToday) cell.setAttribute("aria-current", "date");
			grid.appendChild(cell);
		}
	}

	// The picked day: its events, and the form to add one.
	const dayBox = el("div", "rv-cal-tool");
	const dayTitle = el("h3", "rv-tool-sub");
	const dayList = el("ul", "rv-cal-list");
	const name = input("text", { placeholder: "Event name", maxLength: MAX_TITLE });
	name.setAttribute("aria-label", "Event name");
	const start = input("date", { value: picked });
	const repeat = sel(REPEATS, "none");
	const until = input("date");
	const untilField = field("Ends (optional)", until);
	untilField.hidden = true;
	repeat.addEventListener("change", () => { untilField.hidden = repeat.value === "none"; });
	const msg = el("div", "rv-tnote");
	msg.setAttribute("aria-live", "polite");
	const form = el("form", "rv-cal-form");
	const formRow = el("div", "rv-trow");
	formRow.append(field("Name", name), field("Date", start), field("Repeats", repeat), untilField);
	const add = btn("Add event", null, "rv-tbtn rv-tbtn-accent");
	add.type = "submit";
	form.append(formRow, add, msg);
	form.addEventListener("submit", (e) => {
		e.preventDefault();
		if (events.length >= MAX_EVENTS) { msg.textContent = "That's the most events this calendar keeps. Delete an old one first."; return; }
		const r = makeEvent({ title: name.value, date: start.value, repeat: repeat.value, until: until.value });
		if (r.error) { msg.textContent = r.error; return; }
		events.push(r.event);
		save();
		name.value = ""; until.value = ""; msg.textContent = "";
		announce("Added " + r.event.title + ".");
		picked = r.event.date;
		const d = fromKey(picked);
		year = d.getFullYear(); month = d.getMonth();
		draw(); drawDay();
	});
	function drawDay() {
		const d = fromKey(picked);
		dayTitle.textContent = d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
		start.value = picked;
		dayList.textContent = "";
		const list = eventsOn(events, picked);
		if (!list.length) dayList.appendChild(el("li", "rv-tnote", "Nothing on this day."));
		for (const ev of list) {
			const li = el("li", "rv-cal-ev");
			const text = el("div", "rv-cal-evt");
			text.append(el("span", "rv-cal-evn", ev.title));
			const how = repeatText(ev);
			if (how) text.append(el("span", "rv-cal-evr", how));
			li.append(text);
			const gone = (label, next) => { events = next; save(); announce(label); draw(); drawDay(); };
			if (ev.repeat !== "none") {
				const one = btn("This day", () => gone("Removed " + ev.title + " on this day.", removeDay(events, ev.id, picked)), "rv-tbtn");
				one.setAttribute("aria-label", "Remove " + ev.title + " on this day only");
				li.append(one);
			}
			const all = btn(ev.repeat === "none" ? "Delete" : "All", () => gone("Deleted " + ev.title + ".", removeEvent(events, ev.id)), "rv-tbtn");
			all.setAttribute("aria-label", ev.repeat === "none" ? "Delete " + ev.title : "Delete every " + ev.title);
			li.append(all);
			dayList.appendChild(li);
		}
	}
	dayBox.append(dayTitle, dayList, form, el("p", "rv-tnote", "Events are saved on this device and in your backups."));

	// date difference
	const d1 = input("date", { value: iso(today) });
	const d2 = input("date", { value: iso(today) });
	const diffOut = el("div", "rv-tnote", "");
	diffOut.setAttribute("aria-live", "polite");
	function diff() {
		const days = daysBetween(d1.value, d2.value);
		if (days === null) { diffOut.textContent = ""; return; }
		diffOut.textContent = Math.abs(days) + " day" + (Math.abs(days) === 1 ? "" : "s") + (days < 0 ? " (before)" : days > 0 ? " (after)" : " (same day)");
	}
	[d1, d2].forEach((e) => e.addEventListener("input", diff));

	// add days
	const baseDate = input("date", { value: iso(today) });
	const offset = input("number", { value: "30" });
	const addOut = el("div", "rv-tnote", "");
	addOut.setAttribute("aria-live", "polite");
	function addDays() { const a = addDaysTo(baseDate.value, parseInt(offset.value) || 0); if (a) addOut.textContent = a.toDateString(); }
	[baseDate, offset].forEach((e) => e.addEventListener("input", addDays));

	const diffBox = el("div", "rv-cal-tool"); diffBox.append(el("h3", "rv-tool-sub", "Days between"), el("div", "rv-trow"), diffOut);
	diffBox.querySelector(".rv-trow").append(field("From", d1), field("To", d2));
	const addBox = el("div", "rv-cal-tool"); addBox.append(el("h3", "rv-tool-sub", "Add / subtract days"), el("div", "rv-trow"), addOut);
	addBox.querySelector(".rv-trow").append(field("Date", baseDate), field("Days", offset));

	s.append(head, grid, dayBox, diffBox, addBox);
	draw(); drawDay(); diff(); addDays();
}
